// @intent Pure, deterministic match-3 engine (Match3BackboneSpec §5 [D] helpers) — the single source of truth for swap
//         legality, match detection, clear→gravity→refill→cascade resolution, special spawn/detonation, objective/score
//         progress, and the win/lose decision. The generated playable delegates board state transitions to
//         createMatch3LevelRuntime; UI code only renders/animates the effects.
// @flow board (row-major `cells`; -1 = empty hole) → swap (+ optional detonation) → detectMatches → spawnSpecials →
//        clearAndCollapse → refill (restores FULL enabled-cell count, match-avoiding) → resolveCascades loop →
//        updateScoreObjective → checkEnd.
// @stale 2026-06-11 Б1 — specials layer added (spawn rules, detonation, chain blasts). NO Pixi/DOM/global state
//        (depcruise: playable-api-cannot-import-renderers; types kept LOCAL so the package stays dependency-free and
//        never cycles through shared-types). Keep behaviour identical to services/ai-generate match-topology.ts
//        expectations and the generated-code parity tests.

import { validateMatch3LevelV1, type Match3CoordinateV1, type Match3LevelV1 } from './contracts';

// ---------------------------------------------------------------------------
// Special tile types (Б1)
// ---------------------------------------------------------------------------

/**
 * @intent The kind of a spawned special tile.
 * @flow Determined by match-run shape: 4-in-line → rocket, 5-in-line → colorBomb, 2x2/L/T → bomb.
 * @stale V1.1 Б1 — only 'rocket', 'bomb', 'colorBomb' recognised by the detonation logic.
 */
export type Match3SpecialKind = 'rocket' | 'bomb' | 'colorBomb';

/**
 * @intent Orientation of a rocket special (determines which axis it clears).
 * @flow Set to the orientation of the run that spawned it: 'h' clears the full row, 'v' clears the full column.
 * @stale V1.1 Б1.
 */
export type Match3RocketOrientation = 'h' | 'v';

/**
 * @intent One live special tile: its board index and kind.
 * @flow Held in the runtime's internal specialByIndex map; exposed via getSpecialIndexes().
 * @stale V1.1 Б1 additive.
 */
export interface Match3SpecialEntry {
  readonly index: number;
  readonly kind: Match3SpecialKind;
  /** Only present for 'rocket'; determines row-clear ('h') vs column-clear ('v'). */
  readonly rotation?: Match3RocketOrientation;
}

/** A single board cell coordinate (row + col, row-major). */
export interface Match3Cell {
  readonly row: number;
  readonly col: number;
}

/** The mutable match-3 game board (the live game state the runtime owns + mutates). */
export interface Match3EngineBoard {
  rows: number;
  cols: number;
  /** Row-major tile-type indices in [0, tileTypes); -1 marks an empty hole. */
  cells: number[];
  tileTypes: number;
  /** Row-major; `false` = permanently disabled cell (never filled). Absent = full rectangle. */
  enabledMask?: readonly boolean[];
}

export interface MatchRun {
  readonly orientation: 'h' | 'v';
  readonly length: number;
  readonly cells: readonly Match3Cell[];
}

/** Result of attempting a swap: `ok` = it created a match and stuck; `reverted` = the board was restored. */
export interface SwapResult {
  readonly ok: boolean;
  readonly reverted: boolean;
}

export interface Match3IndexSwap {
  readonly from: number;
  readonly to: number;
}

export interface Match3LevelRuntimeOptions {
  readonly seed?: number;
  readonly firstMove?: {
    readonly a: Match3CoordinateV1;
    readonly b: Match3CoordinateV1;
  };
}

export interface Match3RuntimeSwapResult {
  readonly ok: boolean;
  readonly reverted: boolean;
  readonly fromIndex: number;
  readonly toIndex: number;
  readonly matchedIndexes: readonly number[];
}

export interface Match3RuntimeRefillResult {
  readonly changedIndexes: readonly number[];
}

export interface Match3RuntimeProgressResult {
  readonly scoreDelta: number;
  readonly score: number;
  readonly collected: number;
  readonly objectiveRemaining: number;
}

export interface Match3RuntimeObjectiveTarget {
  readonly index: number;
  readonly row: number;
  readonly col: number;
  readonly role?: string;
  readonly stage: number;
  readonly maxStage: number;
  readonly collected: boolean;
}

export interface Match3RuntimeMoveResult {
  readonly used: number;
  readonly outcome: 'win' | 'lose' | 'none';
}

export interface Match3RuntimeCascadePlan {
  readonly phase: 'clear-refill';
  readonly matchedIndexes: readonly number[];
  readonly boardAfterClear: readonly number[];
  readonly cleared: number;
  readonly progress: Match3RuntimeProgressResult;
  readonly refillChangedIndexes: readonly number[];
  readonly boardAfterRefill: readonly number[];
  readonly repaintIndexes: readonly number[];
  // @intent Specials spawned in this cascade step (additive; absent = none spawned).
  // @flow Filled by spawn rules when a run of length ≥4 is detected.
  // @stale V1.1 Б1 additive.
  readonly spawnedSpecials?: readonly Match3SpecialEntry[];
  // @intent Specials detonated in this cascade step (additive; absent = none detonated).
  // @flow Filled when a blast clears a cell containing a special tile (chaining recorded per step).
  // @stale V1.1 Б1 additive.
  readonly detonatedSpecials?: readonly Match3SpecialEntry[];
}

export interface Match3RuntimeMovePlan {
  readonly result: 'accepted' | 'rejected';
  readonly fromIndex: number;
  readonly toIndex: number;
  readonly acceptedMove: Match3ProbeIndexMove | null;
  readonly rejectedMove: Match3ProbeIndexMove | null;
  readonly initialMatchedIndexes: readonly number[];
  readonly postSwapBoard: readonly number[];
  readonly cascades: readonly Match3RuntimeCascadePlan[];
  readonly repaintIndexes: readonly number[];
  readonly finalBoard: readonly number[];
  readonly outcome: 'win' | 'lose' | 'none';
  // @intent All specials spawned across the full move (additive; absent = none spawned).
  // @flow Aggregated from cascade steps; used by the renderer and probe validator.
  // @stale V1.1 Б1 additive.
  readonly spawnedSpecials?: readonly Match3SpecialEntry[];
  // @intent All specials detonated across the full move (additive; absent = none detonated).
  // @flow Aggregated from cascade steps; used by the renderer and probe validator.
  // @stale V1.1 Б1 additive.
  readonly detonatedSpecials?: readonly Match3SpecialEntry[];
}

export interface Match3ProbeIndexMove {
  readonly fromIndex: number;
  readonly toIndex: number;
}

export interface Match3LevelRuntime {
  readonly rows: number;
  readonly cols: number;
  readonly tileTypes: number;
  readonly board: Match3EngineBoard;
  indexAt(row: number, col: number): number;
  coordAt(index: number): Match3Cell;
  tileTypeAt(index: number): number;
  tileIdForType(type: number): string;
  isSwappableIndex(index: number): boolean;
  firstMoveIndexes(): Match3IndexSwap | null;
  nextMoveIndexes(): Match3IndexSwap | null;
  findValidSwap(): Match3IndexSwap | null;
  matchIndexes(): readonly number[];
  trySwapIndexes(fromIndex: number, toIndex: number): Match3RuntimeSwapResult;
  resolveMoveIndexes(fromIndex: number, toIndex: number): Match3RuntimeMovePlan;
  clearIndexes(indexes: Iterable<number>): number;
  collapseAndRefill(seedChangedIndexes?: Iterable<number>): Match3RuntimeRefillResult;
  recordClear(clearedThisStep: number, matchedIndexes?: Iterable<number>): Match3RuntimeProgressResult;
  completeMove(): Match3RuntimeMoveResult;
  staticCellIndexes(): readonly number[];
  objectiveTargets(): readonly Match3RuntimeObjectiveTarget[];
  objectiveStageProgress(): number;
  objectiveRemaining(): number;
  score(): number;
  used(): number;
  tileCount(): number;
  // @intent Snapshot of all special tiles currently on the board.
  // @flow Returns a fresh array from the internal specialByIndex map; called by the probe/renderer to draw overlays.
  // @stale V1.1 Б1 additive. Returns [] when no specials are present (never undefined).
  getSpecialIndexes(): readonly Match3SpecialEntry[];
  // @intent Snapshot of remaining (uncollected) collectible underlay cells.
  // @flow Returns a fresh array from the internal underlayByIndex map; renderer uses to draw jelly/wafer sprites.
  // @stale V1.1 additive. Returns [] when no underlays remain or none defined.
  getUnderlayIndexes(): readonly { readonly index: number; readonly role: string }[];
}

interface MutableObjectiveTarget {
  readonly index: number;
  readonly row: number;
  readonly col: number;
  readonly role?: string;
  readonly maxStage: number;
  readonly clearRule: 'adjacent-match' | 'matched-cell' | 'scripted';
  stage: number;
  collected: boolean;
}

// Local objective/limit shapes (structurally compatible with shared-types Match3Objective/Match3Limit) — kept local so
// @mintyads/playable-api needs no dependency on @mintyads/shared-types (no cycle).
export type Match3ObjectiveType = 'collectN' | 'scoreTarget' | 'pvpRace' | 'grow';
export interface Match3ObjectiveLike {
  readonly type: Match3ObjectiveType;
  readonly target: number;
  readonly role?: string;
}
export type Match3LimitLike =
  | { readonly kind: 'moves'; readonly value: number }
  | { readonly kind: 'timer'; readonly seconds: number }
  | { readonly kind: 'turns'; readonly value: number }
  | { readonly kind: 'none' };

export interface ObjectiveState {
  score: number;
  collected: number;
  objectiveRemaining: number;
}

const HOLE = -1;
const POINTS_PER_TILE = 10;

/** Seeded PRNG (Mulberry32) — identical to services/ai-generate match-topology.ts so emitted/engine refills can match. */
export function createMatch3Rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function indexOf(cols: number, row: number, col: number): number {
  return row * cols + col;
}
function at(cells: readonly number[], cols: number, row: number, col: number): number {
  return cells[row * cols + col] ?? HOLE;
}

/** True only for orthogonally-adjacent cells (the only legal swap). */
export function areAdjacent(a: Match3Cell, b: Match3Cell): boolean {
  return Math.abs(a.row - b.row) + Math.abs(a.col - b.col) === 1;
}

/** True when the grid contains any horizontal OR vertical 3-in-a-row of identical tile types. */
export function hasAnyThreeInARow(cells: readonly number[], rows: number, cols: number): boolean {
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c + 2 < cols; c += 1) {
      const a = at(cells, cols, r, c);
      if (a >= 0 && a === at(cells, cols, r, c + 1) && a === at(cells, cols, r, c + 2)) return true;
    }
  }
  for (let c = 0; c < cols; c += 1) {
    for (let r = 0; r + 2 < rows; r += 1) {
      const a = at(cells, cols, r, c);
      if (a >= 0 && a === at(cells, cols, r + 1, c) && a === at(cells, cols, r + 2, c)) return true;
    }
  }
  return false;
}

/** First deterministic adjacent swap (row-major scan: right then down) whose application creates a 3-in-a-row. */
export function findMatchMakingSwap(
  cells: readonly number[],
  rows: number,
  cols: number,
): { a: Match3Cell; b: Match3Cell } | undefined {
  const trySwap = (a: Match3Cell, b: Match3Cell): boolean => {
    const next = [...cells];
    const ia = indexOf(cols, a.row, a.col);
    const ib = indexOf(cols, b.row, b.col);
    const tmp = next[ia] ?? 0;
    next[ia] = next[ib] ?? 0;
    next[ib] = tmp;
    return hasAnyThreeInARow(next, rows, cols);
  };
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const here: Match3Cell = { row: r, col: c };
      if (c + 1 < cols) {
        const right: Match3Cell = { row: r, col: c + 1 };
        if (trySwap(here, right)) return { a: here, b: right };
      }
      if (r + 1 < rows) {
        const down: Match3Cell = { row: r + 1, col: c };
        if (trySwap(here, down)) return { a: here, b: down };
      }
    }
  }
  return undefined;
}

/** All maximal horizontal + vertical runs of length >= 3 (holes never match). */
export function detectMatches(board: Match3EngineBoard): MatchRun[] {
  const { rows, cols, cells } = board;
  const runs: MatchRun[] = [];
  for (let r = 0; r < rows; r += 1) {
    let c = 0;
    while (c < cols) {
      const t = at(cells, cols, r, c);
      let end = c;
      while (end + 1 < cols && at(cells, cols, r, end + 1) === t) end += 1;
      const len = end - c + 1;
      if (t >= 0 && len >= 3) {
        const run: Match3Cell[] = [];
        for (let cc = c; cc <= end; cc += 1) run.push({ row: r, col: cc });
        runs.push({ orientation: 'h', length: len, cells: run });
      }
      c = end + 1;
    }
  }
  for (let c = 0; c < cols; c += 1) {
    let r = 0;
    while (r < rows) {
      const t = at(cells, cols, r, c);
      let end = r;
      while (end + 1 < rows && at(cells, cols, end + 1, c) === t) end += 1;
      const len = end - r + 1;
      if (t >= 0 && len >= 3) {
        const run: Match3Cell[] = [];
        for (let rr = r; rr <= end; rr += 1) run.push({ row: rr, col: c });
        runs.push({ orientation: 'v', length: len, cells: run });
      }
      r = end + 1;
    }
  }
  return runs;
}

/** Swap two cells (mutating the board). Keeps the swap iff it creates a match involving a swapped cell; otherwise byte-restores the board. */
export function swap(board: Match3EngineBoard, a: Match3Cell, b: Match3Cell): SwapResult {
  if (!areAdjacent(a, b)) return { ok: false, reverted: true };
  const { cols, cells } = board;
  const ia = indexOf(cols, a.row, a.col);
  const ib = indexOf(cols, b.row, b.col);
  const tmp = cells[ia] ?? HOLE;
  cells[ia] = cells[ib] ?? HOLE;
  cells[ib] = tmp;
  const matchedIndexes = indexesFromRuns(detectMatches(board), cols);
  if (matchedIndexes.includes(ia) || matchedIndexes.includes(ib)) return { ok: true, reverted: false };
  const tmp2 = cells[ia] ?? HOLE;
  cells[ia] = cells[ib] ?? HOLE;
  cells[ib] = tmp2;
  return { ok: false, reverted: true };
}

/**
 * Clear the given cells (set to HOLE) then collapse every column with gravity (survivors settle to the bottom, holes
 * rise to the top). Returns the number of tiles actually cleared. Full-rectangle columns only (masked-barrier collapse
 * is deferred — full-rect is the phase-B default; refill is mask-aware for the enabled-cell count).
 */
export function clearAndCollapse(board: Match3EngineBoard, clearCells: readonly Match3Cell[], gravity: 'down' = 'down'): number {
  void gravity;
  const { rows, cols, cells } = board;
  let cleared = 0;
  for (const cell of clearCells) {
    const i = indexOf(cols, cell.row, cell.col);
    if ((cells[i] ?? HOLE) >= 0) {
      cells[i] = HOLE;
      cleared += 1;
    }
  }
  for (let c = 0; c < cols; c += 1) {
    const survivors: number[] = [];
    for (let r = 0; r < rows; r += 1) {
      const v = at(cells, cols, r, c);
      if (v >= 0) survivors.push(v);
    }
    const holes = rows - survivors.length;
    for (let r = 0; r < rows; r += 1) {
      cells[indexOf(cols, r, c)] = r < holes ? HOLE : (survivors[r - holes] ?? HOLE);
    }
  }
  return cleared;
}

/**
 * Fill every ENABLED hole with a fresh tile that does NOT complete an instant 3-in-a-row (avoiding self-cascading on
 * refill keeps the demo on-rails). Disabled (masked) cells stay holes. Restores the board to its full enabled-cell count
 * — this is the engine-level guarantee that makes the "board disappears" / "no refill" anti-patterns (A1/A3) impossible.
 */
export function refill(board: Match3EngineBoard, rng: () => number): void {
  const { rows, cols, cells, tileTypes, enabledMask } = board;
  const disabled = (i: number): boolean => (enabledMask ? enabledMask[i] === false : false);
  for (let c = 0; c < cols; c += 1) {
    for (let r = 0; r < rows; r += 1) {
      const i = indexOf(cols, r, c);
      if (disabled(i)) {
        cells[i] = HOLE;
        continue;
      }
      if ((cells[i] ?? HOLE) >= 0) continue;
      const start = Math.floor(rng() * tileTypes);
      let chosen = start;
      for (let off = 0; off < tileTypes; off += 1) {
        const cand = (start + off) % tileTypes;
        const completesRow = c >= 2 && at(cells, cols, r, c - 1) === cand && at(cells, cols, r, c - 2) === cand;
        const completesCol = r >= 2 && at(cells, cols, r - 1, c) === cand && at(cells, cols, r - 2, c) === cand;
        if (!completesRow && !completesCol) {
          chosen = cand;
          break;
        }
      }
      cells[i] = chosen;
    }
  }
}

/**
 * Resolve all matches on the board: repeatedly detect → clear+collapse → refill until the board is match-free.
 * Returns the total tiles cleared and the number of cascade rounds. Always terminates (refill avoids instant matches;
 * a generous round bound guards against any pathological loop).
 */
export function resolveCascades(board: Match3EngineBoard, rng: () => number): { totalCleared: number; cascades: number } {
  let totalCleared = 0;
  let cascades = 0;
  const bound = board.rows * board.cols + 16;
  for (let round = 0; round < bound; round += 1) {
    const runs = detectMatches(board);
    if (runs.length === 0) break;
    const toClear: Match3Cell[] = [];
    const seen = new Set<number>();
    for (const run of runs) {
      for (const cell of run.cells) {
        const i = indexOf(board.cols, cell.row, cell.col);
        if (!seen.has(i)) {
          seen.add(i);
          toClear.push(cell);
        }
      }
    }
    totalCleared += clearAndCollapse(board, toClear, 'down');
    refill(board, rng);
    cascades += 1;
  }
  return { totalCleared, cascades };
}

/** Fresh objective state for a brief (score/collected zeroed, remaining = target). */
export function initObjectiveState(objective: Match3ObjectiveLike): ObjectiveState {
  return { score: 0, collected: 0, objectiveRemaining: objective.target };
}

/**
 * Advance score + objective by the tiles cleared in one resolution step. scoreDelta is STRICTLY > 0 whenever tiles were
 * cleared (no fake progress) and 0 when nothing cleared — this is what `match-progress-advances` (§10) asserts.
 */
export function updateScoreObjective(
  state: ObjectiveState,
  clearedThisStep: number,
  objective: Match3ObjectiveLike,
): { scoreDelta: number; objectiveRemaining: number; state: ObjectiveState } {
  if (clearedThisStep <= 0) {
    return { scoreDelta: 0, objectiveRemaining: state.objectiveRemaining, state };
  }
  const next: ObjectiveState = { ...state };
  let scoreDelta: number;
  switch (objective.type) {
    case 'scoreTarget':
    case 'pvpRace':
      scoreDelta = clearedThisStep * POINTS_PER_TILE;
      next.score = state.score + scoreDelta;
      next.objectiveRemaining = Math.max(0, objective.target - next.score);
      break;
    case 'collectN':
    case 'grow':
    default:
      scoreDelta = clearedThisStep;
      next.collected = state.collected + clearedThisStep;
      next.score = state.score + clearedThisStep * POINTS_PER_TILE;
      next.objectiveRemaining = Math.max(0, objective.target - next.collected);
      break;
  }
  return { scoreDelta, objectiveRemaining: next.objectiveRemaining, state: next };
}

/** Win when the objective is met; lose when the limit is exhausted with the objective unmet; else none. `none` never loses. */
export function checkEnd(
  state: ObjectiveState,
  objective: Match3ObjectiveLike,
  limit: Match3LimitLike,
  used: number,
): 'win' | 'lose' | 'none' {
  void objective;
  if (state.objectiveRemaining <= 0) return 'win';
  switch (limit.kind) {
    case 'moves':
    case 'turns':
      return used >= limit.value ? 'lose' : 'none';
    case 'timer':
      return used >= limit.seconds ? 'lose' : 'none';
    case 'none':
    default:
      return 'none';
  }
}

// ---------------------------------------------------------------------------
// Special spawn helpers (Б1)
// ---------------------------------------------------------------------------

/**
 * @intent Detect a 2x2 square among the cells of matched runs.
 * @flow Scans the matched-index set for any 2×2 block where all four corners are present.
 *       Returns the top-left corner index, or -1 if none found.
 * @stale V1.1 Б1.
 */
function find2x2InIndexes(indexes: readonly number[], cols: number): number {
  const set = new Set(indexes);
  for (const i of indexes) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    if (
      set.has(i) &&
      set.has(i + 1) &&
      set.has(i + cols) &&
      set.has(i + cols + 1) &&
      (i + 1) % cols !== 0 // right cell not wrapping to next row
    ) {
      void r; void c;
      return i; // top-left corner of 2x2
    }
  }
  return -1;
}

/**
 * @intent Detect an L or T shape among matched-run cells.
 * @flow An L/T exists when a run of length ≥3 shares ≥1 cell with an orthogonal run of length ≥3 (already in the
 *       runs array). Returns the shared (junction) cell index, or -1 if no L/T found.
 * @stale V1.1 Б1.
 */
function findLTJunctionInRuns(runs: readonly MatchRun[], cols: number): number {
  for (let a = 0; a < runs.length; a += 1) {
    for (let b = a + 1; b < runs.length; b += 1) {
      const ra = runs[a];
      const rb = runs[b];
      if (!ra || !rb) continue;
      if (ra.orientation === rb.orientation) continue; // must be orthogonal
      const setA = new Set(ra.cells.map((cell) => cell.row * cols + cell.col));
      for (const cell of rb.cells) {
        const idx = cell.row * cols + cell.col;
        if (setA.has(idx)) return idx; // junction cell
      }
    }
  }
  return -1;
}

/**
 * @intent Determine which specials are enabled for this level (kind → true).
 * @flow Reads level.specials; absent = no specials (gate for back-compat).
 * @stale V1.1 Б1.
 */
function buildEnabledSpecialKinds(level: Match3LevelV1): Set<string> {
  const enabled = new Set<string>();
  for (const s of level.specials ?? []) {
    enabled.add(s.kind);
  }
  return enabled;
}

/**
 * @intent Compute the spawn cell for a run: prefer the swap cell when it's in the run, else use the middle cell.
 * @flow Called after match detection with the swap-cell indexes available. For cascade spawns swapIndex = -1 → center.
 * @stale V1.1 Б1.
 */
function spawnCellForRun(run: MatchRun, cols: number, swapIndexA: number, swapIndexB: number): number {
  const runIndexes = run.cells.map((cell) => cell.row * cols + cell.col);
  if (swapIndexA >= 0 && runIndexes.includes(swapIndexA)) return swapIndexA;
  if (swapIndexB >= 0 && runIndexes.includes(swapIndexB)) return swapIndexB;
  // cascade or neither swap cell in run → middle cell
  const mid = Math.floor(runIndexes.length / 2);
  return runIndexes[mid] ?? runIndexes[0] ?? 0;
}

// ---------------------------------------------------------------------------
// Detonation helpers (Б1)
// ---------------------------------------------------------------------------

/**
 * @intent Collect all board indexes cleared by detonating a rocket.
 * @flow 'h' rotation → full row of the rocket's position; 'v' rotation → full column.
 * @stale V1.1 Б1.
 */
function rocketBlastIndexes(index: number, rows: number, cols: number, rotation: Match3RocketOrientation): number[] {
  const r = Math.floor(index / cols);
  const c = index % cols;
  const result: number[] = [];
  if (rotation === 'h') {
    for (let cc = 0; cc < cols; cc += 1) result.push(r * cols + cc);
  } else {
    for (let rr = 0; rr < rows; rr += 1) result.push(rr * cols + c);
  }
  return result;
}

/**
 * @intent Collect all board indexes within a square area centred on `index`.
 * @flow radius=1 → 3×3 (bomb); radius=2 → 5×5 (bomb+bomb combo). Clamps to board bounds.
 * @stale V1.1 Б1.
 */
function squareBlastIndexes(index: number, rows: number, cols: number, radius: number): number[] {
  const r = Math.floor(index / cols);
  const c = index % cols;
  const result: number[] = [];
  for (let dr = -radius; dr <= radius; dr += 1) {
    for (let dc = -radius; dc <= radius; dc += 1) {
      const nr = r + dr;
      const nc = c + dc;
      if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) result.push(nr * cols + nc);
    }
  }
  return result;
}

/**
 * @intent Collect all board indexes that hold tiles of a specific tile-type (for colorBomb).
 * @flow Scans board.cells for entries === targetType; -1 (holes) are skipped.
 * @stale V1.1 Б1.
 */
function colorBombBlastIndexes(cells: readonly number[], targetType: number): number[] {
  const result: number[] = [];
  for (let i = 0; i < cells.length; i += 1) {
    if ((cells[i] ?? -1) === targetType) result.push(i);
  }
  return result;
}

function uniqueSortedIndexes(indexes: Iterable<number>, size?: number): number[] {
  const set = new Set<number>();
  for (const index of indexes) {
    if (!Number.isInteger(index)) continue;
    if (index < 0) continue;
    if (size !== undefined && index >= size) continue;
    set.add(index);
  }
  return [...set].sort((a, b) => a - b);
}

function indexesFromRuns(runs: readonly MatchRun[], cols: number): number[] {
  const indexes: number[] = [];
  for (const run of runs) {
    for (const cell of run.cells) indexes.push(indexOf(cols, cell.row, cell.col));
  }
  return uniqueSortedIndexes(indexes);
}

function coordFromIndex(cols: number, index: number): Match3Cell {
  return { row: Math.floor(index / cols), col: index % cols };
}

function readTileType(cellId: string | null, tileIdToType: ReadonlyMap<string, number>): number {
  if (cellId === null) return HOLE;
  return tileIdToType.get(cellId) ?? 0;
}

/** Build the single mutable match-3 runtime model from a strict Match3LevelV1 payload. */
export function createMatch3LevelRuntime(levelInput: Match3LevelV1, options: Match3LevelRuntimeOptions = {}): Match3LevelRuntime {
  const validation = validateMatch3LevelV1(levelInput);
  if (!validation.ok) {
    throw new Error(`Match3LevelV1 runtime validation failed: ${validation.errors.join('; ')}`);
  }
  const level = validation.value;
  const rows = level.board.rows;
  const cols = level.board.cols;
  const size = rows * cols;
  const tileTypes = level.tiles.length;
  const tileIdToType = new Map(level.tiles.map((tile, index) => [tile.id, index] as const));
  const tileIds = level.tiles.map((tile) => tile.id);
  const tileSwappable = level.tiles.map((tile) => tile.swappable !== false);
  const refillTypes = tileSwappable
    .map((swappable, index) => (swappable ? index : -1))
    .filter((index) => index >= 0);
  const enabledMask = level.board.enabledMask ? Array.from(level.board.enabledMask) : Array.from({ length: size }, () => true);
  const blockedIndexes = new Set<number>();
  for (const staticCell of level.board.staticCells ?? []) {
    blockedIndexes.add(indexOf(cols, staticCell.at.row, staticCell.at.col));
  }
  for (const blocker of level.board.blockers ?? []) {
    if (blocker.kind === 'void' || !blocker.swappable || blocker.fillsCell === false) {
      blockedIndexes.add(indexOf(cols, blocker.at.row, blocker.at.col));
    }
  }

  const board: Match3EngineBoard = {
    rows,
    cols,
    tileTypes,
    cells: level.board.cells.map((cellId, index) => (enabledMask[index] === false ? HOLE : readTileType(cellId, tileIdToType))),
    enabledMask,
  };
  const rng = createMatch3Rng(options.seed ?? 1);

  // ---------------------------------------------------------------------------
  // Special tiles state (Б1) — parallel to board.cells, never stored in cells[]
  // Only populated when level.specials declares at least one special kind (back-compat gate).
  // ---------------------------------------------------------------------------
  const enabledSpecialKinds = buildEnabledSpecialKinds(level);
  // Map<boardIndex, Match3SpecialEntry> — mutable parallel state.
  const specialByIndex = new Map<number, Match3SpecialEntry>();
  // Seed pre-placed specials from level.specials[].at (Б4 E3 additive).
  for (const sc of level.specials ?? []) {
    if (sc.at !== undefined && enabledSpecialKinds.has(sc.kind)) {
      const idx = indexOf(cols, sc.at.row, sc.at.col);
      if (idx >= 0 && idx < size) {
        const kind = sc.kind as Match3SpecialKind;
        specialByIndex.set(idx, { index: idx, kind });
      }
    }
  }
  // ---------------------------------------------------------------------------
  // Collectible underlay state (V1.1 additive — absent = no underlay mechanic, back-compat)
  // Map<boardIndex, role> of remaining (uncollected) underlays. Cells with underlays stay swappable.
  // ---------------------------------------------------------------------------
  // @intent Mutable map of remaining underlay cells seeded from board.collectibleUnderlays.
  // @flow Clear path removes entries here; collectN objective counting uses this map.
  // @stale V1.1 additive — empty when level has no collectibleUnderlays (all existing tests unaffected).
  const underlayByIndex = new Map<number, string>();
  for (const underlay of level.board.collectibleUnderlays ?? []) {
    const idx = indexOf(cols, underlay.at.row, underlay.at.col);
    if (idx >= 0 && idx < size) {
      underlayByIndex.set(idx, underlay.role);
    }
  }
  // Number of underlays whose role matches the objective role (for collectN with underlay mechanic).
  // When this is > 0, the collectN objective counts underlay collects (not generic tile clears).
  const underlayObjectiveRole = level.objective.type === 'collectN' ? (level.objective.role ?? null) : null;
  const hasUnderlayObjective =
    underlayObjectiveRole !== null &&
    [...underlayByIndex.values()].some((role) => role === underlayObjectiveRole);

  const objectiveTargets: MutableObjectiveTarget[] = (level.board.staticCells ?? [])
    .filter((cell) => cell.clearRule === 'adjacent-match' || cell.clearRule === 'matched-cell')
    .map((cell) => {
      const index = indexOf(cols, cell.at.row, cell.at.col);
      return {
        index,
        row: cell.at.row,
        col: cell.at.col,
        ...(cell.role === undefined ? {} : { role: cell.role }),
        stage: cell.stage ?? 0,
        maxStage: cell.maxStage ?? 1,
        clearRule: cell.clearRule ?? 'adjacent-match',
        collected: false,
      };
    });
  let objectiveState = initObjectiveState(level.objective);
  if (objectiveTargets.length > 0) {
    objectiveState = { score: 0, collected: 0, objectiveRemaining: level.objective.target };
  }
  let usedCount = 0;

  const inBounds = (index: number): boolean => Number.isInteger(index) && index >= 0 && index < size;
  const coordAt = (index: number): Match3Cell => coordFromIndex(cols, index);
  const indexAtRuntime = (row: number, col: number): number => indexOf(cols, row, col);
  const isBarrierIndex = (index: number): boolean => enabledMask[index] === false || blockedIndexes.has(index);
  const isSwappableIndex = (index: number): boolean => {
    if (!inBounds(index)) return false;
    if (isBarrierIndex(index)) return false;
    const type = board.cells[index] ?? HOLE;
    if (type < 0) return false;
    return tileSwappable[((type % tileSwappable.length) + tileSwappable.length) % tileSwappable.length] !== false;
  };
  const refillTileAt = (row: number, col: number): number => {
    const candidates = refillTypes.length > 0 ? refillTypes : Array.from({ length: tileTypes }, (_unused, index) => index);
    const start = Math.floor(rng() * candidates.length);
    for (let off = 0; off < candidates.length; off += 1) {
      const cand = candidates[(start + off) % candidates.length] ?? 0;
      const completesRow = col >= 2 && at(board.cells, cols, row, col - 1) === cand && at(board.cells, cols, row, col - 2) === cand;
      const completesCol = row >= 2 && at(board.cells, cols, row - 1, col) === cand && at(board.cells, cols, row - 2, col) === cand;
      if (!completesRow && !completesCol) return cand;
    }
    return candidates[start] ?? 0;
  };
  const collapseAndRefillSegments = (seedChangedIndexes: Iterable<number>): Match3RuntimeRefillResult => {
    const before = [...board.cells];
    const changed = new Set(uniqueSortedIndexes(seedChangedIndexes, size));
    for (let col = 0; col < cols; col += 1) {
      let row = 0;
      while (row < rows) {
        const segmentRows: number[] = [];
        while (row < rows) {
          const index = indexOf(cols, row, col);
          if (isBarrierIndex(index)) {
            board.cells[index] = HOLE;
            row += 1;
            break;
          }
          segmentRows.push(row);
          row += 1;
        }
        if (segmentRows.length === 0) continue;
        const survivors = segmentRows
          .map((segmentRow) => board.cells[indexOf(cols, segmentRow, col)] ?? HOLE)
          .filter((value) => value >= 0);
        const missing = segmentRows.length - survivors.length;
        for (let i = 0; i < segmentRows.length; i += 1) {
          const segmentRow = segmentRows[i] ?? 0;
          const index = indexOf(cols, segmentRow, col);
          const next = i < missing ? refillTileAt(segmentRow, col) : (survivors[i - missing] ?? HOLE);
          board.cells[index] = next;
          if (before[index] !== next) changed.add(index);
        }
      }
    }
    for (const index of blockedIndexes) {
      if (inBounds(index)) board.cells[index] = HOLE;
    }
    return { changedIndexes: uniqueSortedIndexes(changed, size) };
  };
  const matchIndexes = (): number[] => indexesFromRuns(detectMatches(board), cols);
  const findValidSwap = (): Match3IndexSwap | null => {
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const from = indexOf(cols, r, c);
        const candidates = [
          c + 1 < cols ? indexOf(cols, r, c + 1) : -1,
          r + 1 < rows ? indexOf(cols, r + 1, c) : -1,
        ];
        for (const to of candidates) {
          if (!isSwappableIndex(from) || !isSwappableIndex(to)) continue;
          const probe: Match3EngineBoard = { ...board, cells: [...board.cells] };
          const result = swap(probe, coordAt(from), coordAt(to));
          if (result.ok) return { from, to };
        }
      }
    }
    return null;
  };
  const firstMoveIndexes = (): Match3IndexSwap | null => {
    if (options.firstMove) {
      return {
        from: indexOf(cols, options.firstMove.a.row, options.firstMove.a.col),
        to: indexOf(cols, options.firstMove.b.row, options.firstMove.b.col),
      };
    }
    return findValidSwap();
  };
  const scriptedMoveIndexes = (move: { readonly a: Match3CoordinateV1; readonly b: Match3CoordinateV1 } | undefined): Match3IndexSwap | null => {
    if (!move) return null;
    return {
      from: indexOf(cols, move.a.row, move.a.col),
      to: indexOf(cols, move.b.row, move.b.col),
    };
  };
  const nextMoveIndexes = (): Match3IndexSwap | null => {
    const script = level.script;
    if (script) {
      if (usedCount === 0) return scriptedMoveIndexes(script.firstMove);
      const nextScriptedMove = script.winningMoves[usedCount - 1];
      if (nextScriptedMove) return scriptedMoveIndexes(nextScriptedMove);
      if (script.allowFreePlayAfterScript === false) return null;
    }
    return findValidSwap();
  };
  const targetSnapshot = (): Match3RuntimeObjectiveTarget[] =>
    objectiveTargets.map((target) => ({
      index: target.index,
      row: target.row,
      col: target.col,
      ...(target.role === undefined ? {} : { role: target.role }),
      stage: target.stage,
      maxStage: target.maxStage,
      collected: target.collected,
    }));
  const objectiveStageProgress = (): number =>
    objectiveTargets.reduce((sum, target) => sum + (target.collected ? target.maxStage + 1 : Math.max(0, target.stage)), 0);
  const targetAt = (row: number, col: number): MutableObjectiveTarget | undefined =>
    objectiveTargets.find((target) => target.row === row && target.col === col && !target.collected);
  const matchesObjectiveRole = (target: MutableObjectiveTarget): boolean =>
    level.objective.role === undefined || target.role === undefined || target.role === level.objective.role;
  const adjacentTargetsForIndex = (index: number): MutableObjectiveTarget[] => {
    const cell = coordAt(index);
    const targets: MutableObjectiveTarget[] = [];
    const offsets = [
      { row: 0, col: -1 },
      { row: -1, col: 0 },
      { row: 1, col: 0 },
      { row: 0, col: 1 },
    ];
    for (const offset of offsets) {
      const row = cell.row + offset.row;
      const col = cell.col + offset.col;
      if (row < 0 || row >= rows || col < 0 || col >= cols) continue;
      const target = targetAt(row, col);
      if (target && target.clearRule === 'adjacent-match' && matchesObjectiveRole(target)) targets.push(target);
    }
    return targets;
  };
  const advanceObjectiveTarget = (target: MutableObjectiveTarget, seen = new Set<number>()): number => {
    if (target.collected || seen.has(target.index)) return 0;
    seen.add(target.index);
    target.stage += 1;
    if (target.stage < target.maxStage) return 0;
    target.collected = true;
    objectiveState = {
      ...objectiveState,
      collected: objectiveState.collected + 1,
      objectiveRemaining: Math.max(0, level.objective.target - (objectiveState.collected + 1)),
    };
    let collected = 1;
    for (const offset of [
      { row: 0, col: -1 },
      { row: -1, col: 0 },
      { row: 1, col: 0 },
      { row: 0, col: 1 },
    ]) {
      const neighbor = targetAt(target.row + offset.row, target.col + offset.col);
      if (neighbor && matchesObjectiveRole(neighbor)) collected += advanceObjectiveTarget(neighbor, seen);
    }
    return collected;
  };
  const advanceObjectiveTargetsFromMatches = (matchedIndexes: Iterable<number> | undefined): number => {
    if (objectiveTargets.length === 0 || matchedIndexes === undefined) return 0;
    let collected = 0;
    for (const index of uniqueSortedIndexes(matchedIndexes, size)) {
      for (const target of adjacentTargetsForIndex(index)) {
        collected += advanceObjectiveTarget(target);
      }
      const ownTarget = targetAt(coordAt(index).row, coordAt(index).col);
      if (ownTarget && ownTarget.clearRule === 'matched-cell' && matchesObjectiveRole(ownTarget)) {
        collected += advanceObjectiveTarget(ownTarget);
      }
    }
    return collected;
  };
  // ---------------------------------------------------------------------------
  // Detonation helpers (internal, Б1)
  // ---------------------------------------------------------------------------

  /**
   * @intent Compute the blast indexes for one special, accounting for combo rules.
   * @flow Special+special combos are resolved by the caller passing `comboPartner`; absent = single detonation.
   * @stale V1.1 Б1.
   */
  const blastIndexesForSpecial = (
    entry: Match3SpecialEntry,
    partnerEntry: Match3SpecialEntry | undefined,
  ): number[] => {
    const { index, kind, rotation } = entry;

    // Special+special combos
    if (partnerEntry !== undefined) {
      const pk = partnerEntry.kind;
      // rocket+rocket → row AND column cross
      if (kind === 'rocket' && pk === 'rocket') {
        const row = rocketBlastIndexes(index, rows, cols, 'h');
        const col = rocketBlastIndexes(index, rows, cols, 'v');
        return uniqueSortedIndexes([...row, ...col]);
      }
      // bomb+bomb → 5x5
      if (kind === 'bomb' && pk === 'bomb') {
        return squareBlastIndexes(index, rows, cols, 2);
      }
      // colorBomb+special → that special's effect applied to every tile of a seeded-random color
      if (kind === 'colorBomb' || pk === 'colorBomb') {
        const colorSeed = Math.floor(rng() * tileTypes);
        const chosenColor = colorSeed % Math.max(1, tileTypes);
        const colorIndexes = colorBombBlastIndexes(board.cells, chosenColor);
        // The other special's pattern is applied AT each chosen-color cell but we simplify to clearing those cells.
        return colorIndexes;
      }
    }

    // Single detonations
    switch (kind) {
      case 'rocket': {
        const rot: Match3RocketOrientation = rotation ?? 'h';
        return rocketBlastIndexes(index, rows, cols, rot);
      }
      case 'bomb':
        return squareBlastIndexes(index, rows, cols, 1);
      case 'colorBomb': {
        // Partner type = the swap-partner tile type at the moment of swap (caller provides via partnerEntry?.index
        // as a special entry whose `index` field encodes the partner board position).
        // When used single (no partner), fall back to a seeded-random color.
        const partnerCellType = partnerEntry !== undefined
          ? (board.cells[partnerEntry.index] ?? -1)
          : -1;
        const targetType = partnerCellType >= 0 ? partnerCellType : Math.floor(rng() * tileTypes) % Math.max(1, tileTypes);
        return colorBombBlastIndexes(board.cells, targetType);
      }
      default:
        return [index];
    }
  };

  /**
   * @intent Execute all detonations triggered by a set of blast-seed indexes, chaining into hit specials.
   * @flow Iterates the pending detonation queue; each detonation adds its blast zone to the clear set and may add
   *       newly-hit specials to the queue (chain detonation). Terminates when no new specials are hit.
   *       `firstPartner` is the combo partner for the first seed only (special+special swap).
   * @stale V1.1 Б1.
   */
  const executeDetonations = (
    seedIndexes: readonly number[],
    firstPartner?: Match3SpecialEntry,
  ): { clearSet: Set<number>; detonated: Match3SpecialEntry[] } => {
    const clearSet = new Set<number>();
    const detonated: Match3SpecialEntry[] = [];
    const queue: Array<{ entry: Match3SpecialEntry; partner: Match3SpecialEntry | undefined }> = [];

    for (let i = 0; i < seedIndexes.length; i += 1) {
      const idx = seedIndexes[i] ?? -1;
      const entry = specialByIndex.get(idx);
      if (entry !== undefined) {
        // First seed gets the combo partner; subsequent seeds are standalone.
        queue.push({ entry, partner: i === 0 ? firstPartner : undefined });
      }
    }

    const visited = new Set<number>();
    while (queue.length > 0) {
      const item = queue.shift();
      if (!item) continue;
      const { entry, partner } = item;
      if (visited.has(entry.index)) continue;
      visited.add(entry.index);
      detonated.push(entry);
      specialByIndex.delete(entry.index);
      const blastIdxs = blastIndexesForSpecial(entry, partner);
      for (const bi of blastIdxs) clearSet.add(bi);
      // Check if any newly-blasted cell holds a special → chain-detonate it.
      for (const bi of blastIdxs) {
        const chainEntry = specialByIndex.get(bi);
        if (chainEntry !== undefined && !visited.has(chainEntry.index)) {
          queue.push({ entry: chainEntry, partner: undefined });
        }
      }
    }
    return { clearSet, detonated };
  };

  const trySwapIndexesRuntime = (fromIndex: number, toIndex: number): Match3RuntimeSwapResult => {
    if (!isSwappableIndex(fromIndex) || !isSwappableIndex(toIndex)) {
      return { ok: false, reverted: true, fromIndex, toIndex, matchedIndexes: [] };
    }

    // Special+special or special+basic detonation check BEFORE swap legality test.
    // A swap involving a special always succeeds (the special detonates instead of matching).
    const fromSpecial = specialByIndex.get(fromIndex);
    const toSpecial = specialByIndex.get(toIndex);

    if (fromSpecial !== undefined || toSpecial !== undefined) {
      // Perform the positional swap so cells move first.
      const tmp = board.cells[fromIndex] ?? HOLE;
      board.cells[fromIndex] = board.cells[toIndex] ?? HOLE;
      board.cells[toIndex] = tmp;

      // Determine detonation seeds and partners.
      let detonationSeeds: number[] = [];
      let partner: Match3SpecialEntry | undefined;

      if (fromSpecial !== undefined && toSpecial !== undefined) {
        // Special+special combo: detonate fromSpecial with toSpecial as the combo partner.
        // toSpecial is removed from the map now so the chain scan won't double-process it.
        partner = toSpecial;
        detonationSeeds = [fromIndex];
        specialByIndex.delete(toIndex);
      } else if (fromSpecial !== undefined) {
        // Special+basic: fromSpecial detonates; for colorBomb the partner encodes the target cell.
        if (fromSpecial.kind === 'colorBomb') {
          partner = { index: toIndex, kind: 'colorBomb' };
        }
        detonationSeeds = [fromIndex];
      } else if (toSpecial !== undefined) {
        if (toSpecial.kind === 'colorBomb') {
          partner = { index: fromIndex, kind: 'colorBomb' };
        }
        detonationSeeds = [toIndex];
      }

      const { clearSet } = executeDetonations(detonationSeeds, partner);
      // Blast clears
      const blastMatchedIndexes = uniqueSortedIndexes(clearSet, size);
      return { ok: true, reverted: false, fromIndex, toIndex, matchedIndexes: blastMatchedIndexes };
    }

    // No specials involved — normal swap.
    const result = swap(board, coordAt(fromIndex), coordAt(toIndex));
    if (!result.ok) return { ok: false, reverted: true, fromIndex, toIndex, matchedIndexes: [] };
    return { ok: true, reverted: false, fromIndex, toIndex, matchedIndexes: matchIndexes() };
  };
  // @intent Track underlay collects that happened in the most-recent clearIndexesRuntime call.
  // @flow clearIndexesRuntime populates this; recordClearRuntime reads it for underlay-objective accounting.
  // @stale V1.1 additive — always [] when no underlays defined.
  let lastClearUnderlayCollects: Array<{ index: number; role: string }> = [];

  const clearIndexesRuntime = (indexes: Iterable<number>): number => {
    lastClearUnderlayCollects = [];
    let cleared = 0;
    for (const index of uniqueSortedIndexes(indexes, size)) {
      if ((board.cells[index] ?? HOLE) >= 0) {
        board.cells[index] = HOLE;
        cleared += 1;
        // Collect the underlay at this index if one exists (V1.1 additive; no-op when map is empty).
        const underlayRole = underlayByIndex.get(index);
        if (underlayRole !== undefined) {
          lastClearUnderlayCollects.push({ index, role: underlayRole });
          underlayByIndex.delete(index);
        }
      }
    }
    return cleared;
  };
  const recordClearRuntime = (
    clearedThisStep: number,
    matchedIndexes?: Iterable<number>,
  ): Match3RuntimeProgressResult => {
    // @intent When a collectN objective targets underlay roles, count collected underlays (not generic tile clears).
    // @flow lastClearUnderlayCollects is populated by clearIndexesRuntime; count matching-role entries here.
    //   For score delta we still award points per tile cleared (keeps score intuitive).
    //   Underlay collect count replaces the collectN increment; generic tile clear increment is suppressed.
    // @stale V1.1 additive — hasUnderlayObjective is false for all existing levels; path is dead-code there.
    if (hasUnderlayObjective && underlayObjectiveRole !== null) {
      const matchingCollects = lastClearUnderlayCollects.filter((c) => c.role === underlayObjectiveRole).length;
      // Score delta: always based on tiles cleared (non-zero when tiles cleared).
      const scoreDelta = clearedThisStep > 0 ? clearedThisStep * 10 : 0;
      const prevScore = objectiveState.score;
      const newScore = prevScore + scoreDelta;
      const newCollected = objectiveState.collected + matchingCollects;
      const newRemaining = Math.max(0, level.objective.target - newCollected);
      objectiveState = { score: newScore, collected: newCollected, objectiveRemaining: newRemaining };
      return { scoreDelta, score: newScore, collected: newCollected, objectiveRemaining: newRemaining };
    }

    const next = updateScoreObjective(objectiveState, clearedThisStep, level.objective);
    objectiveState = objectiveTargets.length > 0
      ? { ...objectiveState, score: next.state.score }
      : next.state;
    if (objectiveTargets.length > 0) {
      advanceObjectiveTargetsFromMatches(matchedIndexes);
    }
    return {
      scoreDelta: next.scoreDelta,
      score: objectiveState.score,
      collected: objectiveTargets.length > 0 ? objectiveState.collected : next.state.collected,
      objectiveRemaining: objectiveState.objectiveRemaining,
    };
  };
  const completeMoveRuntime = (): Match3RuntimeMoveResult => {
    usedCount += 1;
    return { used: usedCount, outcome: checkEnd(objectiveState, level.objective, level.limit, usedCount) };
  };
  // ---------------------------------------------------------------------------
  // Spawn logic (Б1)
  // ---------------------------------------------------------------------------

  /**
   * @intent Process match runs and spawn specials for length ≥4 runs (data-driven gate via enabledSpecialKinds).
   * @flow Called after a swap + match detection. For each run: length ≥5 → colorBomb, length=4 → rocket (run
   *       orientation), then L/T junction → bomb, then 2x2 → bomb. Cascade spawns use run-center cell.
   * @stale V1.1 Б1. No-op when enabledSpecialKinds is empty (back-compat).
   */
  const spawnSpecialsForRuns = (
    runs: readonly MatchRun[],
    swapIndexA: number,
    swapIndexB: number,
  ): Match3SpecialEntry[] => {
    if (enabledSpecialKinds.size === 0) return [];

    const spawned: Match3SpecialEntry[] = [];
    const spawnedAt = new Set<number>();

    // colorBomb: length ≥5 (highest priority)
    if (enabledSpecialKinds.has('colorBomb')) {
      for (const run of runs) {
        if (run.length >= 5) {
          const idx = spawnCellForRun(run, cols, swapIndexA, swapIndexB);
          if (!spawnedAt.has(idx)) {
            spawnedAt.add(idx);
            const entry: Match3SpecialEntry = { index: idx, kind: 'colorBomb' };
            specialByIndex.set(idx, entry);
            spawned.push(entry);
          }
        }
      }
    }

    // rocket: length=4 exactly (or any run 4 where colorBomb didn't spawn)
    if (enabledSpecialKinds.has('rocket')) {
      for (const run of runs) {
        if (run.length === 4) {
          const idx = spawnCellForRun(run, cols, swapIndexA, swapIndexB);
          if (!spawnedAt.has(idx)) {
            spawnedAt.add(idx);
            const entry: Match3SpecialEntry = { index: idx, kind: 'rocket', rotation: run.orientation };
            specialByIndex.set(idx, entry);
            spawned.push(entry);
          }
        }
      }
    }

    // bomb: L/T shape (junction of two orthogonal runs each ≥3)
    if (enabledSpecialKinds.has('bomb')) {
      const ltIdx = findLTJunctionInRuns(runs, cols);
      if (ltIdx >= 0 && !spawnedAt.has(ltIdx)) {
        spawnedAt.add(ltIdx);
        const entry: Match3SpecialEntry = { index: ltIdx, kind: 'bomb' };
        specialByIndex.set(ltIdx, entry);
        spawned.push(entry);
      }
      // bomb: 2x2
      const allMatchedIndexes = runs.flatMap((run) => run.cells.map((cell) => cell.row * cols + cell.col));
      const squareIdx = find2x2InIndexes(allMatchedIndexes, cols);
      if (squareIdx >= 0 && !spawnedAt.has(squareIdx)) {
        spawnedAt.add(squareIdx);
        const entry: Match3SpecialEntry = { index: squareIdx, kind: 'bomb' };
        specialByIndex.set(squareIdx, entry);
        spawned.push(entry);
      }
    }

    return spawned;
  };

  const resolveMoveIndexesRuntime = (fromIndex: number, toIndex: number): Match3RuntimeMovePlan => {
    const boardBeforeSwap = board.cells.slice();

    // Detonation path: if either swap cell holds a special, detonate before standard match check.
    const fromSpecialPre = specialByIndex.get(fromIndex);
    const toSpecialPre = specialByIndex.get(toIndex);
    const isDetonationSwap = fromSpecialPre !== undefined || toSpecialPre !== undefined;

    const accepted = trySwapIndexesRuntime(fromIndex, toIndex);
    if (!accepted.ok || accepted.matchedIndexes.length === 0) {
      return {
        result: 'rejected',
        fromIndex,
        toIndex,
        acceptedMove: null,
        rejectedMove: { fromIndex, toIndex },
        initialMatchedIndexes: [],
        postSwapBoard: boardBeforeSwap,
        cascades: [],
        repaintIndexes: uniqueSortedIndexes([fromIndex, toIndex], size),
        finalBoard: board.cells.slice(),
        outcome: 'none',
      };
    }

    const postSwapBoard = board.cells.slice();
    const cascades: Match3RuntimeCascadePlan[] = [];
    const repaint = new Set<number>(uniqueSortedIndexes([fromIndex, toIndex], size));
    let matchedIndexes = uniqueSortedIndexes(accepted.matchedIndexes, size);

    // For detonation swaps, track which specials were detonated in the first step.
    const firstStepDetonated: Match3SpecialEntry[] = [];
    if (isDetonationSwap) {
      if (fromSpecialPre !== undefined) firstStepDetonated.push(fromSpecialPre);
      if (toSpecialPre !== undefined) firstStepDetonated.push(toSpecialPre);
    }

    const allSpawned: Match3SpecialEntry[] = [];
    const allDetonated: Match3SpecialEntry[] = [...firstStepDetonated];

    const bound = size + 16;
    for (let cascadeIndex = 0; cascadeIndex < bound && matchedIndexes.length > 0; cascadeIndex += 1) {
      for (const index of matchedIndexes) repaint.add(index);

      // Spawn specials for this cascade's matched runs (skip for detonation-initiated first cascade).
      let stepSpawned: Match3SpecialEntry[] = [];
      let stepDetonated: Match3SpecialEntry[] = [];

      // Indexes that hold a newly-spawned special — these cells are NOT cleared this step.
      const spawnedAtSet = new Set<number>();

      if (!isDetonationSwap || cascadeIndex > 0) {
        // Detect runs on current board state to determine shapes.
        const currentRuns = detectMatches(board);
        // Spawn from current runs (use swap cells for step 0, -1/-1 for cascades).
        const swapA = cascadeIndex === 0 ? fromIndex : -1;
        const swapB = cascadeIndex === 0 ? toIndex : -1;
        stepSpawned = spawnSpecialsForRuns(currentRuns, swapA, swapB);
        // The spawn cell stays on the board — remove it from the clear set.
        for (const sp of stepSpawned) spawnedAtSet.add(sp.index);
      } else if (cascadeIndex === 0) {
        stepDetonated = firstStepDetonated;
      }

      // Build the actual clear set: matched minus spawn cells.
      const clearIndexes = matchedIndexes.filter((idx) => !spawnedAtSet.has(idx));

      // Clear: remove specials that are in the clear set (blasted by detonation or matched).
      const detonatedThisStep: Match3SpecialEntry[] = [];
      for (const idx of clearIndexes) {
        const sp = specialByIndex.get(idx);
        // Only count as detonated if not already tracked (avoid double-counting chain detonations).
        if (sp !== undefined && !allDetonated.some((d) => d.index === idx)) {
          detonatedThisStep.push(sp);
          specialByIndex.delete(idx);
        }
      }
      if (detonatedThisStep.length > 0) stepDetonated = [...stepDetonated, ...detonatedThisStep];

      const cleared = clearIndexesRuntime(clearIndexes);
      const boardAfterClear = board.cells.slice();
      const progress = recordClearRuntime(cleared, clearIndexes);
      const refillResult = collapseAndRefillSegments(clearIndexes);
      const boardAfterRefill = board.cells.slice();
      for (const index of refillResult.changedIndexes) repaint.add(index);
      const repaintIndexes = uniqueSortedIndexes([...clearIndexes, ...refillResult.changedIndexes], size);

      const cascadeEntry: Match3RuntimeCascadePlan = {
        phase: 'clear-refill',
        matchedIndexes,
        boardAfterClear,
        cleared,
        progress,
        refillChangedIndexes: refillResult.changedIndexes,
        boardAfterRefill,
        repaintIndexes,
        ...(stepSpawned.length > 0 ? { spawnedSpecials: stepSpawned } : {}),
        ...(stepDetonated.length > 0 ? { detonatedSpecials: stepDetonated } : {}),
      };
      cascades.push(cascadeEntry);

      if (stepSpawned.length > 0) allSpawned.push(...stepSpawned);
      if (stepDetonated.length > 0) allDetonated.push(...stepDetonated);

      matchedIndexes = uniqueSortedIndexes(matchIndexes(), size);
    }

    const end = completeMoveRuntime();
    return {
      result: 'accepted',
      fromIndex,
      toIndex,
      acceptedMove: { fromIndex, toIndex },
      rejectedMove: null,
      initialMatchedIndexes: uniqueSortedIndexes(accepted.matchedIndexes, size),
      postSwapBoard,
      cascades,
      repaintIndexes: uniqueSortedIndexes(repaint, size),
      finalBoard: board.cells.slice(),
      outcome: end.outcome,
      ...(allSpawned.length > 0 ? { spawnedSpecials: allSpawned } : {}),
      ...(allDetonated.length > 0 ? { detonatedSpecials: allDetonated } : {}),
    };
  };

  return {
    rows,
    cols,
    tileTypes,
    board,
    indexAt: indexAtRuntime,
    coordAt,
    tileTypeAt(index) {
      return inBounds(index) ? (board.cells[index] ?? HOLE) : HOLE;
    },
    tileIdForType(type) {
      return tileIds[((type % tileIds.length) + tileIds.length) % tileIds.length] ?? tileIds[0] ?? 'tile-0';
    },
    isSwappableIndex,
    firstMoveIndexes,
    nextMoveIndexes,
    findValidSwap,
    matchIndexes,
    trySwapIndexes: trySwapIndexesRuntime,
    resolveMoveIndexes: resolveMoveIndexesRuntime,
    clearIndexes: clearIndexesRuntime,
    collapseAndRefill(seedChangedIndexes = []) {
      return collapseAndRefillSegments(seedChangedIndexes);
    },
    recordClear: recordClearRuntime,
    completeMove: completeMoveRuntime,
    staticCellIndexes() {
      return uniqueSortedIndexes(blockedIndexes, size);
    },
    objectiveTargets() {
      return targetSnapshot();
    },
    objectiveStageProgress,
    objectiveRemaining() {
      return objectiveState.objectiveRemaining;
    },
    score() {
      return objectiveState.score;
    },
    used() {
      return usedCount;
    },
    tileCount() {
      return board.cells.filter((cell) => cell >= 0).length;
    },
    getSpecialIndexes() {
      return [...specialByIndex.values()];
    },
    getUnderlayIndexes() {
      return [...underlayByIndex.entries()].map(([index, role]) => ({ index, role }));
    },
  };
}
